import { FC, useMemo, useState } from "react"
import {
  Alert,
  Button,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { Visibility } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useSnackbar } from "notistack"
import { UserbotCatalog, UserbotTemplates } from "../../interfaces"
import {
  useCreateTemplateMutation,
  usePreviewTemplateMutation,
  useUpdateTemplateMutation,
} from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { VariablesEditor } from "./variablesEditor"
import { coerceFieldValue } from "../fields/fieldSpecCoerce"
import { isValidJson, parseJsonValue, stringifyJson } from "../common/jsonUtils"
import { getApiErrorMessage } from "@/core/utils"

interface IProps {
  template?: UserbotTemplates.Template
}

const KIND_OPTIONS: { value: UserbotTemplates.TemplateKind; label: string }[] =
  [
    { value: "chain", label: "Цепочка" },
    { value: "trigger", label: "Триггер" },
    { value: "scheduled", label: "Расписание" },
  ]

export const TemplateEditor: FC<IProps> = ({ template }) => {
  const isEdit = !!template
  const canWrite = useUserbotWriteAccess()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [name, setName] = useState(template?.name ?? "")
  const [description, setDescription] = useState(template?.description ?? "")
  const [category, setCategory] = useState(template?.category ?? "")
  const [kind, setKind] = useState<UserbotTemplates.TemplateKind>(
    template?.kind ?? "chain",
  )
  const [isActive, setIsActive] = useState(template?.isActive ?? true)
  const [variables, setVariables] = useState<UserbotCatalog.FieldSpec[]>(
    template?.variables ?? [],
  )
  const [bodyJson, setBodyJson] = useState(
    template ? stringifyJson(template.body) : "{\n  \n}",
  )
  const [repeatBlocksJson, setRepeatBlocksJson] = useState(
    template?.repeatBlocks ? stringifyJson(template.repeatBlocks) : "",
  )
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] =
    useState<UserbotTemplates.TemplatePreviewRes | null>(null)

  const [createTemplate, createState] = useCreateTemplateMutation()
  const [updateTemplate, updateState] = useUpdateTemplateMutation()
  const [previewTemplate, { isLoading: isPreviewing }] =
    usePreviewTemplateMutation()
  const loading = createState.isLoading || updateState.isLoading

  const testInputs = useMemo(() => {
    const out: Record<string, unknown> = {}
    for (const v of variables) {
      if (v.default !== undefined) out[v.key] = coerceFieldValue(v, v.default)
    }
    return out
  }, [variables])

  const validate = (): string | null => {
    if (!name.trim()) return "Укажите название"
    if (!category.trim()) return "Укажите категорию"
    if (!isValidJson(bodyJson)) return "body — некорректный JSON"
    if (repeatBlocksJson.trim() && !isValidJson(repeatBlocksJson)) {
      return "repeatBlocks — некорректный JSON"
    }
    if (variables.some((v) => !v.key.trim() || !v.label.trim())) {
      return "У каждой переменной должны быть key и label"
    }
    return null
  }

  const buildDto = (): UserbotTemplates.CreateTemplateDto => {
    const dto: UserbotTemplates.CreateTemplateDto = {
      name: name.trim(),
      category: category.trim(),
      kind,
      variables,
      body:
        (parseJsonValue<Record<string, unknown>>(bodyJson) as Record<
          string,
          unknown
        >) ?? {},
      isActive,
    }
    if (description.trim()) dto.description = description.trim()
    const rb = parseJsonValue<UserbotTemplates.RepeatBlock[]>(repeatBlocksJson)
    if (rb) dto.repeatBlocks = rb
    return dto
  }

  const handleSave = async () => {
    setError(null)
    const err = validate()
    if (err) {
      setError(err)
      return
    }
    try {
      if (isEdit) {
        await updateTemplate({ id: template!.id, body: buildDto() }).unwrap()
        enqueueSnackbar("Шаблон обновлён", { variant: "success" })
      } else {
        await createTemplate({ body: buildDto() }).unwrap()
        enqueueSnackbar("Шаблон создан", { variant: "success" })
      }
      navigate("/userbot/templates")
    } catch (e) {
      setError(getApiErrorMessage(e))
    }
  }

  const handlePreview = async () => {
    if (!isEdit) return
    try {
      const res = await previewTemplate({
        id: template!.id,
        body: { inputs: testInputs },
      }).unwrap()
      setPreview(res)
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e), { variant: "error" })
    }
  }

  if (!canWrite) {
    return (
      <Alert severity="warning">
        Недостаточно прав для редактирования шаблонов.
      </Alert>
    )
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {isEdit && (
        <Typography variant="caption" color="text.secondary">
          Версия: v{template!.version}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="Название"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Описание"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Категория"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              sx={{ flex: 1 }}
            />
            <TextField
              select
              label="Тип"
              value={kind}
              onChange={(e) =>
                setKind(e.target.value as UserbotTemplates.TemplateKind)
              }
              disabled={isEdit}
              sx={{ minWidth: 180 }}
            >
              {KIND_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Активен"
            />
          </Stack>
        </Stack>
      </Paper>

      <Divider textAlign="left">
        <Typography variant="subtitle2">Переменные</Typography>
      </Divider>
      <VariablesEditor variables={variables} onChange={setVariables} />

      <Divider textAlign="left">
        <Typography variant="subtitle2">Тело шаблона (body)</Typography>
      </Divider>
      <TextField
        label="body (JSON)"
        value={bodyJson}
        onChange={(e) => setBodyJson(e.target.value)}
        multiline
        minRows={8}
        fullWidth
        error={bodyJson.trim() !== "" && !isValidJson(bodyJson)}
        helperText="Плейсхолдеры переменных: {{input.<key>}}"
        InputProps={{ style: { fontFamily: "monospace" } }}
      />

      <Divider textAlign="left">
        <Typography variant="subtitle2">
          repeatBlocks (опционально, JSON)
        </Typography>
      </Divider>
      <TextField
        label="repeatBlocks (JSON)"
        value={repeatBlocksJson}
        onChange={(e) => setRepeatBlocksJson(e.target.value)}
        multiline
        minRows={4}
        fullWidth
        error={repeatBlocksJson.trim() !== "" && !isValidJson(repeatBlocksJson)}
        InputProps={{ style: { fontFamily: "monospace" } }}
      />

      {preview && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" mb={1}>
            Предпросмотр DTO
          </Typography>
          {preview.validation &&
            (preview.validation.valid ? (
              <Alert severity="success" sx={{ mb: 1 }}>
                Цепочка валидна
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 1 }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {preview.validation.errors.map((iss, i) => (
                    <li key={i}>
                      {iss.stepId ? `[${iss.stepId}] ` : ""}
                      {iss.message}
                    </li>
                  ))}
                </ul>
              </Alert>
            ))}
          <TextField
            value={JSON.stringify(preview.dto, null, 2)}
            multiline
            fullWidth
            InputProps={{ readOnly: true, style: { fontFamily: "monospace" } }}
          />
        </Paper>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        {isEdit && (
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handlePreview}
            disabled={isPreviewing}
          >
            Проверить (Preview)
          </Button>
        )}
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          Сохранить
        </Button>
      </Stack>
    </Stack>
  )
}
